import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import PageHero from '@/components/PageHero';
import FadeInUp from '@/components/FadeInUp';
import { BUSINESS, COURSE_OPTIONS } from '@/lib/site';
import { PhoneIcon, MailIcon, ClockIcon, FaxIcon, CheckIcon, ArrowRight } from '@/components/icons';

function encode(data) {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(data[key] === true ? 'Yes' : data[key] === false ? 'No' : data[key] ?? ''))
    .join('&');
}

export default function Booking() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { on_site: 'not_sure', nzqa: false, f_endorsement: false, course: '' }
  });

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'booking-enquiry',
          ...data,
        }),
      });
      setSubmitted(true);
      reset();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error('Could not submit enquiry. Please try again or call us.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Hidden form for Netlify build-time detection (required for React SPAs) */}
      <form name="booking-enquiry" data-netlify="true" data-netlify-honeypot="bot-field" hidden>
        <input type="text" name="name" />
        <input type="text" name="company" />
        <input type="email" name="email" />
        <input type="tel" name="phone" />
        <input type="text" name="course" />
        <input type="number" name="trainees" />
        <input type="text" name="preferred_date" />
        <input type="text" name="on_site" />
        <input type="text" name="nzqa" />
        <input type="text" name="f_endorsement" />
        <textarea name="message"></textarea>
      </form>

      <PageHero
        eyebrow="Bookings & Enquiries"
        title="Book a course — or ask us anything."
        subtitle="Submit the form, give us a call, or send an email. WINZ quotations available on request."
        image="/generated/course-standard.png"
        breadcrumbs={[{label:'Home', to:'/'}, {label:'Booking'}]}
      />

      <section className="nft-container py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Contact methods */}
          <aside className="lg:col-span-4 space-y-4">
            <FadeInUp>
              <div className="surface-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center">
                    <PhoneIcon width={20} height={20} />
                  </div>
                  <div className="label-eyebrow text-[#FC4E2C]">Phone</div>
                </div>
                <a href={BUSINESS.phoneHref} className="block font-display font-extrabold uppercase text-2xl text-[#060D9C] hover:text-[#FC4E2C]">
                  {BUSINESS.phone}
                </a>
                <div className="mt-1 text-xs text-[#060D9C]/60 flex items-center gap-1">
                  <ClockIcon width={12} height={12} /> {BUSINESS.hours}
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.05}>
              <div className="surface-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center">
                    <PhoneIcon width={20} height={20} />
                  </div>
                  <div className="label-eyebrow text-[#FC4E2C]">Urgent / After Hours</div>
                </div>
                <a href={BUSINESS.mobileHref} className="block font-display font-extrabold uppercase text-2xl text-[#060D9C] hover:text-[#FC4E2C]">
                  Text {BUSINESS.mobile}
                </a>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.1}>
              <div className="surface-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center">
                    <MailIcon width={20} height={20} />
                  </div>
                  <div className="label-eyebrow text-[#FC4E2C]">Email</div>
                </div>
                <a href={BUSINESS.emailHref} className="block font-bold text-[#060D9C] hover:text-[#FC4E2C] break-all">
                  {BUSINESS.email}
                </a>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.15}>
              <div className="surface-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#FC4E2C]/15 text-[#FC4E2C] flex items-center justify-center">
                    <FaxIcon width={20} height={20} />
                  </div>
                  <div className="label-eyebrow text-[#FC4E2C]">Fax</div>
                </div>
                <span className="font-bold text-[#060D9C]">{BUSINESS.fax}</span>
              </div>
            </FadeInUp>

            <div className="callout-steel">
              <div className="label-eyebrow text-[#FC4E2C] mb-1">WINZ Quotations</div>
              <p className="text-sm text-[#060D9C]/85">
                Available on request. Include your case manager's details in the message field.
              </p>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-8">
            {submitted ? (
              <FadeInUp>
                <div className="surface-card p-8" data-testid="booking-success-state">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#2D8653]/15 text-[#2D8653] flex items-center justify-center">
                      <CheckIcon width={28} height={28} />
                    </div>
                    <div className="flex-1">
                      <div className="label-eyebrow text-[#2D8653] mb-1">Enquiry Received</div>
                      <h2 className="font-display font-extrabold uppercase text-3xl text-[#060D9C] leading-tight">
                        Thanks for getting in touch!
                      </h2>
                      <p className="mt-3 text-[#060D9C]/85">
                        We've received your enquiry and will be in touch shortly.
                      </p>
                      <p className="mt-4 text-sm text-[#060D9C]/70">
                        For urgent bookings, please call <a href={BUSINESS.phoneHref} className="font-bold text-[#060D9C] underline">{BUSINESS.phone}</a> or text{' '}
                        <a href={BUSINESS.mobileHref} className="font-bold text-[#060D9C] underline">{BUSINESS.mobile}</a>.
                      </p>
                      <button
                        onClick={() => setSubmitted(false)}
                        className="mt-6 btn-secondary-navy"
                        data-testid="booking-submit-another"
                      >
                        Submit another enquiry
                      </button>
                    </div>
                  </div>
                </div>
              </FadeInUp>
            ) : (
              <FadeInUp>
                <form
                  name="booking-enquiry"
                  method="POST"
                  data-netlify="true"
                  data-netlify-honeypot="bot-field"
                  onSubmit={handleSubmit(onSubmit)}
                  className="surface-card p-6 lg:p-8 space-y-5"
                  data-testid="booking-form"
                  noValidate
                >
                  <input type="hidden" name="form-name" value="booking-enquiry" />
                  <p hidden><input name="bot-field" /></p>

                  <div>
                    <div className="label-eyebrow text-[#FC4E2C] mb-1">Booking Enquiry</div>
                    <h2 className="font-display font-extrabold uppercase text-3xl text-[#060D9C] leading-tight">
                      Tell us what you need.
                    </h2>
                    <p className="mt-2 text-sm text-[#060D9C]/70">
                      Required fields marked with <span className="text-[#FC4E2C]">*</span>
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Full Name *" error={errors.name?.message}>
                      <input
                        type="text"
                        {...register('name', { required: 'Required' })}
                        className="form-input"
                        data-testid="booking-form-name"
                      />
                    </Field>
                    <Field label="Company Name">
                      <input type="text" {...register('company')} className="form-input" data-testid="booking-form-company" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Email *" error={errors.email?.message}>
                      <input
                        type="email"
                        {...register('email', { required: 'Required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' } })}
                        className="form-input"
                        data-testid="booking-form-email"
                      />
                    </Field>
                    <Field label="Phone">
                      <input type="tel" {...register('phone')} className="form-input" data-testid="booking-form-phone" />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Course Interested In *" error={errors.course?.message}>
                      <select
                        {...register('course', { required: 'Required' })}
                        className="form-input"
                        data-testid="booking-form-course"
                        defaultValue=""
                      >
                        <option value="" disabled>Select a course…</option>
                        {COURSE_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Number of Trainees">
                      <input
                        type="number"
                        min={1}
                        max={200}
                        {...register('trainees')}
                        className="form-input"
                        data-testid="booking-form-trainees"
                      />
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Preferred Date / Timeframe">
                      <input
                        type="text"
                        placeholder="e.g. Week of 15 Apr, or ASAP"
                        {...register('preferred_date')}
                        className="form-input"
                        data-testid="booking-form-preferred-date"
                      />
                    </Field>
                    <Field label="On-site Training Required?">
                      <select
                        {...register('on_site')}
                        className="form-input"
                        data-testid="booking-form-onsite"
                      >
                        <option value="not_sure">Not sure</option>
                        <option value="yes">Yes — at our site</option>
                        <option value="no">No — at NFT premises</option>
                      </select>
                    </Field>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <CheckField label="Add NZQA Unit Standard assessment" name="nzqa" register={register} testid="booking-form-nzqa" />
                    <CheckField label='Add "F" Endorsement' name="f_endorsement" register={register} testid="booking-form-fendorse" />
                  </div>

                  <Field label="Message / Special Requirements">
                    <textarea
                      rows={5}
                      {...register('message')}
                      className="form-input min-h-[140px]"
                      placeholder="Tell us about your team, your equipment, WINZ details, or anything else relevant."
                      data-testid="booking-form-message"
                    />
                  </Field>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                    <p className="text-xs text-[#060D9C]/60">
                      We typically respond within one business day during {BUSINESS.hours}.
                    </p>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn-primary-orange disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="booking-form-submit-button"
                    >
                      {submitting ? 'Sending…' : 'Submit Enquiry'} {!submitting && <ArrowRight width={16} height={16} />}
                    </button>
                  </div>
                </form>
              </FadeInUp>
            )}
          </div>
        </div>
      </section>
      <style>{`
        .form-input {
          width: 100%;
          padding: 0.7rem 0.9rem;
          border-radius: 0.75rem;
          border: 1.5px solid rgba(26,36,181,0.25);
          background: #fff;
          color: #060D9C;
          font-family: 'Lato', sans-serif;
          font-size: 0.95rem;
          transition: border-color 180ms ease, box-shadow 180ms ease;
        }
        .form-input:focus {
          outline: none;
          border-color: #FC4E2C;
          box-shadow: 0 0 0 3px rgba(252,78,44,0.18);
        }
        .form-input::placeholder { color: rgba(6,13,156,0.4); }
      `}</style>
    </>
  );
}

function Field({ label, children, error }) {
  return (
    <label className="block">
      <span className="block label-eyebrow text-[#060D9C]/80 mb-1.5">{label}</span>
      {children}
      {error && <span className="block mt-1 text-xs text-[#c0392b]">{error}</span>}
    </label>
  );
}

function CheckField({ label, name, register, testid }) {
  return (
    <label className="flex items-start gap-3 surface-card p-3 cursor-pointer hover:border-[#FC4E2C]/50 transition-colors">
      <input type="checkbox" {...register(name)} className="mt-1 w-4 h-4 accent-[#FC4E2C]" data-testid={testid} />
      <span className="text-sm text-[#060D9C]/85 font-bold">{label}</span>
    </label>
  );
}
